import { ChangeDetectionStrategy, Component, NgZone, OnDestroy, OnInit, inject, signal } from '@angular/core';

import { BaseComponent } from '../../../core/abstractions/base.component';
import { Tools } from '../../../core/utils/tools';
import { ChatMessageModel } from '../../../models/chat-message.model';
import { SignalRChannelEnum } from '../../../models/enums/signalr-channel.enum';
import { OptionalType } from '../../../models/types/optional.type';
import { AlertService } from '../../../services/alert.service';
import { GeneralMeetingsResource } from '../../../services/resources/general-meetings.resource';
import { SignalRService } from '../../../services/signalr.service';
import { TranslationService } from '../../../services/translation.service';

@Component({
    selector: 'sl-chat-control',
    templateUrl: './chat-control.component.html',
    styleUrls: ['./chat-control.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ChatControlComponent extends BaseComponent implements OnInit, OnDestroy {
    isLoading = true;
    currentLocaleCode = 'de';
    readonly selectedShareholderId = signal<OptionalType<string>>(undefined);
    readonly currentMessages = signal<ChatMessageModel[]>([]);
    readonly currentConversations = signal<ChatMessageModel[]>([]);
    readonly isLoadingConversation = signal(false);
    readonly isExportingCsv = signal(false);

    private readonly ngZone = inject(NgZone);
    private readonly generalMeetingsResource = inject(GeneralMeetingsResource);
    private readonly signalRService = inject(SignalRService);
    private readonly translationService = inject(TranslationService);
    private readonly alertService = inject(AlertService);

    constructor() {
        super();
        this.currentLocaleCode = this.translationService.getCurrent().code;
    }

    ngOnInit(): void {
        this.selectOverview();

        const msgSub = this.signalRService.onMessageReceived.subscribe(shareholderId => {
            this.ngZone.run(() => {
                // Reload overview
                this.selectOverview();

                // reload selected conversation (if the selected was updated)
                // use double equals because the types might be different (number / string)
                // eslint-disable-next-line eqeqeq
                if (shareholderId == this.selectedShareholderId()) {
                    this.selectConversation(shareholderId);
                }
            });
        });

        this.signalRService.startConnection(SignalRChannelEnum.Chat, '0');
        this.addSubscriptions(msgSub);

        this.isLoading = false;
    }

    selectOverview(): void {
        this.generalMeetingsResource.getCurrentChat().subscribe(r => this.currentConversations.set(r.value));
    }

    override ngOnDestroy(): void {
        super.ngOnDestroy();
        this.signalRService.stopConnection();
    }

    selectConversation(shareholderId: string): void {
        this.isLoadingConversation.set(true);
        this.selectedShareholderId.set(shareholderId);
        this.generalMeetingsResource.getChatWith(shareholderId).subscribe(r => {
            this.currentMessages.set(r.value);
            this.isLoadingConversation.set(false);
        });

        // Need to get the conversations again, so that the list is updated reflecting the message which has been read.
        this.selectOverview();
    }

    exportChat(): void {
        this.isExportingCsv.set(true);
        this.generalMeetingsResource.exportChat().subscribe(r => {
            const blob = new Blob(['\ufeff', r], { type: 'text/csv;charset=utf-8' });

            Tools.Utils.saveFile(blob, 'chat.csv');
            this.isExportingCsv.set(false);
        });
    }

    deleteChat(): void {
        this.alertService.showConfirm('Chat.AreYouSureRemove', undefined, () => {
            this.generalMeetingsResource.deleteChat().subscribe(() => this.selectOverview());
        });
    }
}
